import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { useStudentRecoveryQuestions } from '@/api/client'
import { AuthShell } from '@/components/auth-shell'
import { ForcedPasswordChange } from '@/components/forced-password-change'
import { StudentRecoverySetup } from '@/components/student-recovery-setup'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authApi, isRateLimited, type AuthResponse } from '@/lib/auth-api'
import { sessionFromResponse, sessionQuery } from '@/lib/auth'
import { isValidStudentLogin, normalizeStudentLogin } from '@/lib/student-login'
import { studentLoginPendingStep, type StudentLoginPendingStep } from '@/lib/student-login-guidance'

export const Route = createFileRoute('/uczen/login')({
  validateSearch: (search: Record<string, unknown>): { expired?: 'ok' } => ({
    expired: search.expired === 'ok' ? 'ok' : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    if (session.isAuthenticated) {
      throw redirect({ to: '/uczen' })
    }
  },
  component: StudentLoginPage,
})

// TR-200 §3.1: tylko login i hasło; bez rejestracji i bez linku resetu.
const studentLoginSchema = z.object({
  username: z
    .string()
    .min(1, 'Podaj login')
    .refine(
      isValidStudentLogin,
      'Login ma 3 segmenty: KOD_SZKOŁY-KOD_KLASY-NUMER, np. AGR01-EUR-12.',
    ),
  password: z.string().min(1, 'Podaj hasło'),
})

function StudentLoginPage() {
  const { expired } = Route.useSearch()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<'credentials' | StudentLoginPendingStep>('credentials')
  const [loginError, setLoginError] = useState<string | null>(null)

  const form = useForm({
    resolver: zodResolver(studentLoginSchema),
    defaultValues: { username: '', password: '' },
  })
  const usernameField = form.register('username')
  const username = useWatch({ control: form.control, name: 'username' })

  const questionsQuery = useStudentRecoveryQuestions({
    query: {
      enabled: step === 'recovery-setup',
      staleTime: 60 * 60 * 1000,
    },
  })
  const questionResponse = questionsQuery.data
  const questions = questionResponse?.status === 200 ? questionResponse.data.questions : []

  const finishLogin = (response: AuthResponse) => {
    queryClient.setQueryData(sessionQuery.queryKey, sessionFromResponse(response))
    router.history.push('/uczen')
  }

  const continuePendingFlow = (response: AuthResponse): boolean => {
    const pendingStep = studentLoginPendingStep(response)
    if (!pendingStep) return false
    setLoginError(null)
    setStep(pendingStep)
    return true
  }

  const backToCredentials = async () => {
    await authApi.logout()
    form.reset()
    setLoginError(null)
    setStep('credentials')
  }

  const submit = form.handleSubmit(async ({ username, password }) => {
    setLoginError(null)
    const response = await authApi.login({ username, password })
    if (response.meta?.is_authenticated) {
      finishLogin(response)
    } else if (continuePendingFlow(response)) {
      return
    } else if (isRateLimited(response)) {
      setLoginError('Zbyt wiele prób logowania. Spróbuj ponownie później.')
    } else {
      setLoginError('Nieprawidłowy login lub hasło.')
    }
  })

  return (
    <AuthShell>
      <Card>
        {step === 'credentials' ? (
          <>
            <CardHeader>
              <CardTitle>Zaloguj się</CardTitle>
              <CardDescription>
                Wpisz login i hasło otrzymane od nauczyciela. Login ma format kod szkoły–kod
                klasy–numer ucznia, np. AGR01-EUR-12.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
                {expired === 'ok' && (
                  <p className="text-sm text-muted-foreground">
                    Sesja wygasła. Zaloguj się ponownie.
                  </p>
                )}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="username">Login</Label>
                  <Input
                    id="username"
                    {...usernameField}
                    value={username}
                    onChange={(event) =>
                      form.setValue('username', normalizeStudentLogin(event.target.value), {
                        shouldDirty: true,
                        shouldValidate: form.formState.isSubmitted,
                      })
                    }
                    placeholder="np. AGR01-EUR-12"
                    autoComplete="username"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    autoFocus
                  />
                  {form.formState.errors.username && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.username.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password">Hasło</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    {...form.register('password')}
                  />
                  {form.formState.errors.password && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.password.message}
                    </p>
                  )}
                </div>
                {loginError && <p className="text-sm text-destructive">{loginError}</p>}
                <p className="text-sm text-muted-foreground">
                  Nie pamiętasz hasła? Zresetuj je w aplikacji mobilnej albo poproś nauczyciela o
                  pomoc.
                </p>
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  Zaloguj
                </Button>
              </form>
            </CardContent>
          </>
        ) : step === 'password-change' ? (
          <ForcedPasswordChange
            onSuccess={finishLogin}
            onIntermediate={continuePendingFlow}
            onLogout={backToCredentials}
          />
        ) : (
          <StudentRecoverySetup
            questions={questions}
            questionsPending={questionsQuery.isPending}
            questionsFailed={
              questionsQuery.isError ||
              (questionResponse !== undefined && questionResponse.status !== 200)
            }
            onSuccess={finishLogin}
            onLogout={backToCredentials}
          />
        )}
      </Card>
    </AuthShell>
  )
}
